/**
 * GA4 (gtag.js) — privacy-safe, no-op-safe analytics for the Pranto.Gfx SPA.
 *
 * - Enabled ONLY when REACT_APP_GA4_MEASUREMENT_ID is a valid `G-XXXXXXXX` id.
 *   When unset (current state until the client provides the ID) every function
 *   is a silent no-op, so the app renders and behaves identically.
 * - Never sends PII: query strings / hashes are stripped from page paths, and
 *   custom events only accept a fixed allow-list of scalar, non-identifying
 *   parameter keys. Form fields, emails, phone numbers, message text, tokens
 *   and Mongo ids are never forwarded.
 */

const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;

export const isAnalyticsEnabled =
    typeof window !== "undefined" && /^G-[A-Z0-9]+$/i.test(measurementId || "");

let initialized = false;

// Fixed allow-list of non-PII parameter keys we permit on custom events.
const ALLOWED_PARAM_KEYS = new Set([
    "placement",
    "source",
    "platform",
    "direction",
    "category",
    "item_id",
    "kind",
    "form",
    "action",
]);

const isScalar = (v) =>
    typeof v === "string" || typeof v === "number" || typeof v === "boolean";

export function initGA4() {
    if (!isAnalyticsEnabled || initialized) return;

    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);

    window.gtag("js", new Date());
    // We emit page_view explicitly on every React Router location change.
    window.gtag("config", measurementId, { send_page_view: false });
    initialized = true;
}

export function pageView(location) {
    if (!isAnalyticsEnabled || !window.gtag) return;
    // Strip query + hash: they can carry PII (emails, tokens, codes).
    const path = `${location?.pathname || window.location.pathname}`;
    window.gtag("event", "page_view", {
        page_title: document.title,
        page_path: path,
        page_location: `${window.location.origin}${path}`,
    });
}

export function trackEvent(name, params = {}) {
    if (!isAnalyticsEnabled || !window.gtag) return;
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(name)) return;

    const cleaned = Object.fromEntries(
        Object.entries(params).filter(
            ([key, value]) => ALLOWED_PARAM_KEYS.has(key) && isScalar(value),
        ),
    );
    window.gtag("event", name, cleaned);
}

const OUTBOUND = new Set(["whatsapp", "behance", "instagram", "linkedin"]);

export function trackOutbound(platform) {
    if (OUTBOUND.has(platform)) {
        trackEvent("outbound_click", { platform });
    }
}
